/**
 * <Link> wrapper for any element that LAUNCHES a drill (i.e., routes to
 * a fresh chat that would consume from the user's coach-response cap on
 * the first turn). When the user is over their cap, intercepts the
 * click and pops the upgrade modal instead of routing.
 *
 * Without this, capped users navigate to the chat page and only see
 * the 402-triggered modal AFTER they've left their current surface —
 * forcing them to back-button out of an unusable chat screen. Popping
 * the modal pre-emptively keeps them where they were so dismiss
 * (or "View pricing") just continues their flow.
 *
 * If billing status hasn't loaded yet, the link behaves normally;
 * the existing ChatView 402 catch is the safety net.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { type CSSProperties, type MouseEvent, type ReactNode } from "react";

import {
  getBillingStatus,
  isHardWall,
  isOverLimit,
} from "@/features/billing/lib/billing";
import { useUpgradeModal } from "@/features/billing/components/UpgradeModal";

interface DrillLaunchLinkProps {
  to: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  title?: string;
  "aria-label"?: string;
  /**
   * When true, only pre-empt on the free HARD WALL — a paid soft cap is
   * allowed through. Used by surfaces that are exempt from the metered
   * monthly cap but NOT from the free wall (the daily challenge): a
   * subscriber over their monthly allowance can still play it, but a
   * free user past their first report can't.
   */
  softCapExempt?: boolean;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

export function DrillLaunchLink({
  to,
  children,
  onClick,
  softCapExempt = false,
  ...rest
}: DrillLaunchLinkProps) {
  const { data: status } = useQuery({
    queryKey: ["billing-status"],
    queryFn: getBillingStatus,
    staleTime: 60_000,
  });
  const { open } = useUpgradeModal();

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // Pre-empt the launch when the user is walled. Normally any over-limit
    // state blocks (free hard wall OR paid soft cap); `softCapExempt`
    // narrows it to the hard wall only, so cap-exempt surfaces (the daily
    // challenge) still launch for a soft-capped subscriber.
    if (status && (softCapExempt ? isHardWall(status) : isOverLimit(status))) {
      e.preventDefault();
      open({
        window: status.gatingWindow,
        gateType: status.gateType,
        subscriptionStatus: status.subscriptionStatus,
        planLevel: status.planLevel,
        used: status.used,
        limit: status.limit ?? 0,
      });
      return;
    }
    onClick?.(e);
  };

  return (
    <Link to={to} onClick={handleClick} {...rest}>
      {children}
    </Link>
  );
}
